import {
  CLASSIC_OPTIONS, courseUses, DEBRIS_OPTIONS, PAIRING_EFFECTS, PLAYBACK_MS, playbackMs, ROBOT_COLOURS,
  SIDE_MANOEUVRES,
} from '@roborally/engine';
import type { CourseFeatures, OptionEffect } from '@roborally/engine';
import type { GameSettings, OptionCardView } from './api.js';

/**
 * How a host sets a game up, apart from the course: what the New Game
 * screen's settings hold, and what a saved game setup keeps.
 *
 * Kept in the units the screen shows rather than the ones the server
 * takes, so a setup opens as it was saved: a turn-mode clock of 24 hours
 * comes back as 24 hours, not 86400 seconds.
 */

/** Turn mode's timer units, in seconds. */
export const TURN_TIMER_UNIT_SECONDS = { seconds: 1, minutes: 60, hours: 3600 } as const;
export type TurnTimerUnit = keyof typeof TURN_TIMER_UNIT_SECONDS;

export interface GameSetup {
  /** The host's own robot. Null is one at random. */
  colour: string | null;
  maxPlayers: number;
  livesOn: boolean;
  lifeTokens: number;
  turnMode: boolean;
  optionsEnabled: boolean;
  /** Which Options are in the deck. Null is every card of the mode. */
  chosenOptions: string[] | null;
  startingOptions: number;
  debrisEnabled: boolean;
  lateJoin: boolean;
  /** Turn mode only, the one mode a bot plays. */
  botsAllowed: boolean;
  timerOn: boolean;
  /** Normal mode's clock. */
  timerSeconds: number;
  /** Turn mode's clock, in the units a host thinks of it in. */
  turnTimerAmount: number;
  turnTimerUnit: TurnTimerUnit;
  /** How the clock is spent, not how long it runs. */
  timerMode: 'hand' | 'last';
  /** Milliseconds between steps of a register's playback. */
  playbackMs: number;
}

/** A new game with no saved setup. Options and debris are base rules and
 * start on; joining and bots start open. */
export const DEFAULT_SETUP: Readonly<GameSetup> = {
  colour: null,
  maxPlayers: 8,
  livesOn: true,
  lifeTokens: 3,
  turnMode: false,
  optionsEnabled: true,
  chosenOptions: null,
  startingOptions: 0,
  debrisEnabled: true,
  lateJoin: true,
  botsAllowed: true,
  timerOn: false,
  timerSeconds: 120,
  turnTimerAmount: 24,
  turnTimerUnit: 'hours',
  timerMode: 'hand',
  playbackMs: PLAYBACK_MS,
};

/** The cards a mode deals from: normal mode's are the catalog less the
 * turn cards; turn mode's are the turn cards and every card that never
 * interrupts. The server's `optionIdsForMode` draws the same line. A
 * game without debris deals nothing that works on it, and a course,
 * when there is one, nothing it gives no use. */
export function modeCards(
  catalog: OptionCardView[], turnMode: boolean, debris = true, course?: CourseFeatures,
): OptionCardView[] {
  return catalog.filter((o) => (turnMode ? o.turnMode || !o.interrupts : !o.turnMode)
    && (debris || !DEBRIS_OPTIONS.has(o.effect as OptionEffect))
    && (!course || courseUses(o.effect as OptionEffect, course)));
}

/** The multicard Options: the four that pair cards and the side
 * manoeuvres, in either mode's form. */
export function isMulticard(o: OptionCardView): boolean {
  return PAIRING_EFFECTS.has(o.effect as OptionEffect)
    || SIDE_MANOEUVRES[o.effect as OptionEffect] !== undefined;
}

/** The classic Options: the original game's, not Off the Grid's. */
export function isClassic(o: OptionCardView): boolean {
  return CLASSIC_OPTIONS.has(o.effect as OptionEffect);
}

/** A setup as the game it would make, for `settingsLine`. */
export function settingsOfSetup(s: GameSetup, catalog: OptionCardView[]): GameSettings {
  return {
    maxPlayers: s.maxPlayers,
    optionsEnabled: s.optionsEnabled,
    optionCount: s.chosenOptions?.length ?? null,
    optionCatalog: modeCards(catalog, s.turnMode, s.debrisEnabled).length,
    startingOptions: s.startingOptions,
    debrisEnabled: s.debrisEnabled,
    turnMode: s.turnMode,
    timerSeconds: !s.timerOn ? null
      : s.turnMode ? s.turnTimerAmount * TURN_TIMER_UNIT_SECONDS[s.turnTimerUnit]
        : s.timerSeconds,
    timerMode: s.timerMode,
    lateJoin: s.lateJoin,
    botsAllowed: s.botsAllowed,
    playbackMs: s.playbackMs,
    lifeTokens: s.livesOn ? s.lifeTokens : 0,
  };
}

/**
 * A saved setup read back, every field checked. One that is missing or
 * the wrong kind takes the default, so a setup saved before a setting
 * existed still opens.
 */
export function setupFrom(saved: unknown): GameSetup {
  const s = (saved && typeof saved === 'object' ? saved : {}) as Record<string, unknown>;
  const d = DEFAULT_SETUP;
  const bool = (k: keyof GameSetup) => (typeof s[k] === 'boolean' ? s[k] as boolean : d[k] as boolean);
  const num = (k: keyof GameSetup) => (typeof s[k] === 'number' && Number.isFinite(s[k]) ? s[k] as number : d[k] as number);
  return {
    colour: typeof s.colour === 'string' && (ROBOT_COLOURS as readonly string[]).includes(s.colour)
      ? s.colour : null,
    maxPlayers: num('maxPlayers'),
    livesOn: bool('livesOn'),
    lifeTokens: num('lifeTokens'),
    turnMode: bool('turnMode'),
    optionsEnabled: bool('optionsEnabled'),
    chosenOptions: Array.isArray(s.chosenOptions) && s.chosenOptions.every((x) => typeof x === 'string')
      ? s.chosenOptions as string[] : null,
    startingOptions: num('startingOptions'),
    debrisEnabled: bool('debrisEnabled'),
    lateJoin: bool('lateJoin'),
    botsAllowed: bool('botsAllowed'),
    timerOn: bool('timerOn'),
    timerSeconds: num('timerSeconds'),
    turnTimerAmount: num('turnTimerAmount'),
    turnTimerUnit: typeof s.turnTimerUnit === 'string' && s.turnTimerUnit in TURN_TIMER_UNIT_SECONDS
      ? s.turnTimerUnit as TurnTimerUnit : d.turnTimerUnit,
    timerMode: s.timerMode === 'last' ? 'last' : 'hand',
    playbackMs: playbackMs(s.playbackMs),
  };
}
